import App from './application-container';
import { animate, unhighlight_currently_highlighted_object } from './scene';
import * as shaders from './shaders';
import * as THREE from 'three';
import HashTable from './hashtable';
import { urls } from '@phi/constants';
import { display_hide_hidden_edges, display_show_hidden_edges, uid_calculate_global_bounding_box } from './ui_display';
import { pipe, compose } from '@vaaas/fpts/function';
import { filter, bind, each, map } from '@vaaas/fpts/iter';
import { inside } from '@vaaas/fpts/array';
import { object_is_selected, suppress_animate, unsuppress_animate_if_ready } from './utility';
import { edge_is_for_sketch, face_is_for_sketch, vertex_is_for_sketch } from './reference_sketches';
import { uil_can_make_edges_symmetric, uil_is_object_visible } from './ui_logic';
import { reference_transparent } from '../../../../src/features/references';

let reflectionCube = undefined;

export function set_default_edge_color(eid) {
  if (uid_state.shaders_on)
    return;

  if (isEdge(eid))  {
    var uido = uid_edges.getItem(eid);

    // Set color based on whether it is linear and/or selected
    var r = uido.color_r;
    var g = uido.color_g;
    var b = uido.color_b;

    // Instead of using the standard linearity flag for the edge to decide on the coloring, we'll use the
    // geometric linearity. This allows us to show as linear edges from which we were forced to remove the linearity flag due
    // to tangencies on them.
    if (uil_is_edge_linear_geometry(uido.id) || uil_is_edge_linear(uido.id)) {
      r = color_linear_edge_r;
      g = color_linear_edge_g;
      b = color_linear_edge_b;
    }
    if (uil_is_object_fixed(eid)) {
      r = color_fixed_r;
      g = color_fixed_g;
      b = color_fixed_b;
    }
    if (object_is_selected(eid)) {
      r = App.state.colours.selected.r;
      g = App.state.colours.selected.g;
      b = App.state.colours.selected.b;
    }
    if (!object_is_selected(eid) && (symmetry_edges_list_first.includes(uido.id) || symmetry_edges_list_second.includes(uido.id))) {
      if (symmetry_plane_base && symmetry_plane_normal) {
        // Check that symmetry can be applied
        let edge_symmetry_set_index = symmetry_edges_list_first.includes(uido.id) ?
              symmetry_edges_list_first.indexOf(uido.id) :
              symmetry_edges_list_second.indexOf(uido.id);
        let symmetric_edge_id = symmetry_edges_list_first.includes(uido.id) ?
              symmetry_edges_list_second[edge_symmetry_set_index] :
              symmetry_edges_list_first[edge_symmetry_set_index];
        let symmetry_generally_possible =
          uil_can_make_edges_symmetric(uido.id, symmetric_edge_id, symmetry_plane_base, symmetry_plane_normal) &&
          uil_can_make_edges_symmetric(symmetric_edge_id, uido.id, symmetry_plane_base, symmetry_plane_normal);
        if (!symmetry_generally_possible) {
          r = App.state.colours.edges.symmetric.error.r;
          g = App.state.colours.edges.symmetric.error.g;
          b = App.state.colours.edges.symmetric.error.b;
          // Also force color to opposite side
        }
        else if (edge_position_wrt_plane(symmetry_plane_base, symmetry_plane_normal, uido.id, 1e-3) > 0) {
          r = App.state.colours.edges.symmetric.first.r;
          g = App.state.colours.edges.symmetric.first.g;
          b = App.state.colours.edges.symmetric.first.b;
        }
        else {
          r = App.state.colours.edges.symmetric.second.r;
          g = App.state.colours.edges.symmetric.second.g;
          b = App.state.colours.edges.symmetric.second.b;
        }
      }
      else {
        console_log_xml("Edges in symmetry set but no symmetry plane selected");
      }
    }
    if (consistency_problem_edge_list.indexOf(uido.id) != -1) {
      r = color_consistency_problem_r;
      g = color_consistency_problem_g;
      b = color_consistency_problem_b;
    }

    if (!object_is_selected(eid) && edge_is_for_sketch(uido.id)) {
      if (App.state.editing_reference_sketches) {
        r = color_sketch_edge_r;
        g = color_sketch_edge_g;
        b = color_sketch_edge_b;
        uido.get_visible_edge_mesh().material.opacity = 1.0;
        uido.get_visible_edge_mesh().material.transparent = false;
      }
      else {
        r = color_sketch_edge_soft_r;
        g = color_sketch_edge_soft_g;
        b = color_sketch_edge_soft_b;
        uido.get_visible_edge_mesh().material.opacity = 0.2;
        uido.get_visible_edge_mesh().material.transparent = true;
      }
    }

    set_visible_edge_color(uido, r / 256, g / 256, b / 256, false);
    uido.object3d.material.color.setRGB( r / 256, g / 256, b / 256);
    return;
  }

  if (uid_reference_edges.hasItem(eid)) {
    uido = uid_reference_edges.getItem(eid);

    var r;
    var g;
    var b;
    var re = uid_reference_edges.getItem(eid).uid_object;

    if (!reference_transparent.value) {
      r = ref_solid_face_color_r;
      g = ref_solid_face_color_g;
      b = ref_solid_face_color_b;
    }
    else if (re.source == "stl") {
      r = ref_stl_face_color_r;
      g = ref_stl_face_color_g;
      b = ref_stl_face_color_b;
    }
    else if (re.source == "parasolid") {
      r = ref_parasolid_face_color_r;
      g = ref_parasolid_face_color_g;
      b = ref_parasolid_face_color_b;
    }
    else /* if (re.source == "partstudio") */ {
      r = ref_partstudio_face_color_r;
      g = ref_partstudio_face_color_g;
      b = ref_partstudio_face_color_b;
    }

    if (reference_object_is_selected(eid)) {
      r = App.state.colours.selected.r;
      g = App.state.colours.selected.g;
      b = App.state.colours.selected.b;
    }

    set_visible_edge_color(uido.uid_object, r / 256 , g / 256 , b / 256, false);
    uido.uid_object.object3d.material.color.setRGB( r / 256, g / 256, b / 256);
    return;
  }
}

export function set_visible_edge_color(uido, r, g, b, is_highlighted = false) {
  var colors = [];
  uido.object3d.geometry.vertices.forEach(function(v){
    colors.push( r, g, b );
  });
  uido.get_visible_edge_mesh().geometry.setColors(colors);
}


export function set_default_face_color(fid) {
  if (isFace(fid)) {

    if (uid_state.shaders_on)
      return;

    var uido = uid_faces.getItem(fid);
    var r = uido.color_r;
    var g = uido.color_g;
    var b = uido.color_b;
    var innerr, innerg, innerb;
    if (App.state.inside_red && !face_is_for_sketch(fid)) {
      innerr = 255;
      innerg = 68;
      innerb = 68;
    } else {
      innerr = r;
      innerg = g;
      innerb = b;
    }

    if (uil_is_object_fixed(fid)) {
      r = color_fixed_r;
      g = color_fixed_g;
      b = color_fixed_b;
    }
    if (object_is_selected(fid)) {
      r = App.state.colours.selected.r;
      g = App.state.colours.selected.g;
      b = App.state.colours.selected.b;
      var innerr = r;
      var innerg = g;
      var innerb = b;
    }

    if (consistency_problem_face_list.indexOf(uido.id) != -1) {

      r = color_consistency_problem_face_r;
      g = color_consistency_problem_face_g;
      b = color_consistency_problem_face_b;

      // Create consistency problem material

      /*
      var consistency_problem_glow_material = new THREE.ShaderMaterial(
        {
            uniforms:
          {
            "c":   { type: "f", value: 0.1 },
            "p":   { type: "f", value: 3 },
            glowColor: { type: "c", value: new THREE.Color(0xffbc00) },
            viewVector: { type: "v3", value: uid_camera.position }
          },
          vertexShader:   document.getElementById( 'vertexShader'   ).textContent,
          fragmentShader: document.getElementById( 'fragmentShader' ).textContent,
          //side: THREE.BackSide,
          side: THREE.FrontSide,
          blending: THREE.AdditiveBlending,
          transparent: true
        }
      );

      var myGlow = new THREE.Mesh( uido.object3d.geometry, consistency_problem_glow_material );
      myGlow.scale.multiplyScalar(1.1);
      uid_glow_raynode.add( myGlow );
      */
    }

    uido.getMaterial().color.setRGB( r /256, g /256, b /256 );
    if (uido.getMirrorMaterial())
      uido.getMirrorMaterial().color.setRGB( r /256, g /256, b /256 );

    if (uido.getInnerMaterial())
      uido.getInnerMaterial().color.setRGB( innerr /256, innerg /256, innerb /256 );
    if (uido.getMirrorInnerMaterial())
      uido.getMirrorInnerMaterial().color.setRGB( innerr /256, innerg /256, innerb /256 );
  }
  else if (uid_reference_faces.hasItem(fid)) {
    uido = uid_reference_faces.getItem(fid);
    var r;
    var g;
    var b;
    var re = uid_reference_faces.getItem(fid);

    if (!reference_transparent.value) {
      r = ref_solid_face_color_r;
      g = ref_solid_face_color_g;
      b = ref_solid_face_color_b;
    }
    else if (re.source == "stl") {
      r = ref_stl_face_color_r;
      g = ref_stl_face_color_g;
      b = ref_stl_face_color_b;
    }
    else if (re.source == "parasolid") {
      r = ref_parasolid_face_color_r;
      g = ref_parasolid_face_color_g;
      b = ref_parasolid_face_color_b;
    }
    else /* if (re.source == "partstudio") */ {
      r = ref_partstudio_face_color_r;
      g = ref_partstudio_face_color_g;
      b = ref_partstudio_face_color_b;
    }

    if (reference_object_is_selected(fid)) {
      r = App.state.colours.selected.r;
      g = App.state.colours.selected.g;
      b = App.state.colours.selected.b;
    }
    uido.uid_object.object3d.material.color.setRGB( r /256, g /256, b /256 );

    // Set transparency state
    if (reference_transparent.value) {
      uido.uid_object.object3d.material.opacity = 0.5;
      uido.uid_object.object3d.material.transparent = true;
      uido.uid_object.object3d.material.shininess = 80;
    }
    else {
      uido.uid_object.object3d.material.opacity = 1;
      uido.uid_object.object3d.material.transparent = false;
      uido.uid_object.object3d.material.shininess = 30;
    }
  }
}

export function set_default_vertex_color (vid, is_highlighted = false) {
  if (isVertex(vid)) {
    var uido = uid_vertices.getItem(vid);
    if (is_highlighted)
      uido.object3d.material.color.setRGB(App.state.colours.highlighted.r/256, App.state.colours.highlighted.g/256, App.state.colours.highlighted.b/256);
    else if (object_is_selected(vid))
      uido.object3d.material.color.setRGB( App.state.colours.selected.r  / 256, App.state.colours.selected.g / 256, App.state.colours.selected.b / 256 );
    else if (uil_is_object_fixed(vid))
      uido.object3d.material.color.setRGB(color_fixed_r/256, color_fixed_g/256, color_fixed_b/256);
    else if (vertex_is_for_sketch(vid)) {
      if (App.state.editing_reference_sketches) {
        uido.object3d.material.color.setRGB(color_sketch_vertex_r/256,color_sketch_vertex_g/256,color_sketch_vertex_b/256);
        uido.object3d.material.opacity = 1.0;
        uido.object3d.material.transparent = false;
      }
      else {
        uido.object3d.material.color.setRGB(color_sketch_vertex_soft_r/256,color_sketch_vertex_soft_g/256,color_sketch_vertex_soft_b/256);
        uido.object3d.material.opacity = 0.0;
        uido.object3d.material.transparent = true;
      }
    }
    else if (vertex_has_undeclared_tangency(vid)) {
      uido.object3d.material.color.setRGB(color_undeclared_vertex_tangency_r/256, color_undeclared_vertex_tangency_g/256, color_undeclared_vertex_tangency_b/256);

      var consistency_problem_glow_material = new THREE.ShaderMaterial({
        uniforms: {
          "c": { type: "f", value: 0.1 },
          "p": { type: "f", value: 3 },
          glowColor: { type: "c", value: new THREE.Color(0xff3300) },
          viewVector: { type: "v3", value: uid_camera.position },
        },
        vertexShader: shaders.vertexShader,
        fragmentShader: shaders.fragmentShader,
        //side: THREE.BackSide,
        side: THREE.FrontSide,
        blending: THREE.AdditiveBlending,
        transparent: true,
      });
      var myGlow = new THREE.Mesh( uido.object3d.geometry, consistency_problem_glow_material );
      myGlow.scale.multiplyScalar(3);
      myGlow.translateX(uido.object3d.position.x);
      myGlow.translateY(uido.object3d.position.y);
      myGlow.translateZ(uido.object3d.position.z);
      uid_glow_raynode.add( myGlow );
    }
    else
      uido.object3d.material.color.setRGB( uido.color_r / 256, uido.color_g / 256, uido.color_b / 256 );
  }
  else if (uid_reference_vertices.hasItem(vid)) {
    uido = uid_reference_vertices.getItem(vid);
    var r, g, b;
    if (reference_object_is_selected(vid)){
      r = App.state.colours.selected.r;
      g = App.state.colours.selected.g;
      b = App.state.colours.selected.b;
    }
    else {
      var re = uid_reference_vertices.getItem(vid);
      if (is_highlighted) {
        r = App.state.colours.highlighted.r;
        g = App.state.colours.highlighted.g;
        b = App.state.colours.highlighted.b;
      }
      else if (!reference_transparent.value) {
        r = ref_solid_face_color_r;
        g = ref_solid_face_color_g;
        b = ref_solid_face_color_b;
      }
      else if (re.source == "stl") {
        r = ref_stl_face_color_r;
        g = ref_stl_face_color_g;
        b = ref_stl_face_color_b;
      }
      else if (re.source == "parasolid") {
        r = ref_parasolid_face_color_r;
        g = ref_parasolid_face_color_g;
        b = ref_parasolid_face_color_b;
      }
      else /* if (re.source == "partstudio") */ {
        r = ref_partstudio_face_color_r;
        g = ref_partstudio_face_color_g;
        b = ref_partstudio_face_color_b;
      }
      uido.uid_object.object3d.material.color.setRGB( r / 256, g / 256, b / 256 );
    }
  }
}

//----------------------- Lighting, colors, materials
export function switch_to_lights(light_style) {
  // Remove all lights
  for (var i = uid_lights_raynode.children.length - 1; i >= 0; i--) {
    uid_lights_raynode.remove(uid_lights_raynode.children[i]);
  }

  // Add specific lights
  if (light_style == "disco") {
    var ambientLight = new THREE.AmbientLight( 0xffffff, 0.2 );
    uid_lights_raynode.add( ambientLight );
    var pointLight = new THREE.PointLight( 0xaa2222, 0.5 );
    pointLight.position.z = 2500;
    uid_lights_raynode.add( pointLight );
    var pointLight2 = new THREE.PointLight( 0xff6666, 0.4 );
    uid_lights_raynode.add( pointLight2 );
    var pointLight3 = new THREE.PointLight( 0x2222aa, 0.5 );
    pointLight3.position.x = - 1000;
    pointLight3.position.z = 1000;
    uid_lights_raynode.add( pointLight3 );
    var directionalLight = new THREE.DirectionalLight(0xffffff, 0.5);
    directionalLight.position.set(0, 50, 50);
    uid_lights_raynode.add(directionalLight);
    var light1 = new THREE.DirectionalLight(0xffffff, 0.4);
    light1.position.set(-5, -5, -5);
    uid_lights_raynode.add(light1);
    var light2 = new THREE.DirectionalLight(0x888888, 0.5);
    light2.position.set(5, 5, 5);
    uid_lights_raynode.add(light2);
    var light3 = new THREE.DirectionalLight(0x999999, 0.3);
    light3.position.set(-20, 20, 20);
    uid_lights_raynode.add(light3);
    var light4 = new THREE.DirectionalLight(0x888888, 0.4);
    light4.position.set(5, -5, 5);
    uid_lights_raynode.add(light4);
    var light5 = new THREE.DirectionalLight(0x888888, 0.5);
    light5.position.set(5, 5, -5);
    uid_lights_raynode.add(light5);
  }
  else if (light_style == "urban") {

    var ambientLight = new THREE.AmbientLight( 0x8C968F, 0.6 );
    uid_lights_raynode.add( ambientLight );

    var pointLight1 = new THREE.PointLight( 0xA79F95, 0.5 );
    pointLight1.position.set( 50, 50, 50 );
    uid_lights_raynode.add( pointLight1 );

    var pointLight2 = new THREE.PointLight( 0xA79F95, 0.5 );
    pointLight2.position.set( 50, -50, 50 );
    uid_lights_raynode.add( pointLight2 );

    var pointLight3 = new THREE.PointLight( 0xA79F95, 0.5 );
    pointLight3.position.set( -50, 0, 50 );
    uid_lights_raynode.add( pointLight3 );

    var pointLight4 = new THREE.PointLight( 0xA79F95&6, 0.5 );
    pointLight4.position.set( -50, 0, -50 );
    uid_lights_raynode.add( pointLight4 );

    var light1 = new THREE.DirectionalLight(0x777777, 0.3);
    light1.position.set(-50, -50, -50);
    uid_lights_raynode.add(light1);
    var light2 = new THREE.DirectionalLight(0x555555, 0.3);
    light2.position.set(-50, 50, -50);
    uid_lights_raynode.add(light2);
    var light3 = new THREE.DirectionalLight(0x666666, 0.3);
    light3.position.set(-50, -50, 50);
    uid_lights_raynode.add(light3);
    var light4 = new THREE.DirectionalLight(0x555555, 0.3);
    light4.position.set(-50, 50, 50);
    uid_lights_raynode.add(light4);
    var light5 = new THREE.DirectionalLight(0x888888, 0.3);
    light5.position.set(50, 0, 50);
    uid_lights_raynode.add(light5);
  }
  else if (light_style == "regular") {
//    ambientLight = new THREE.HemisphereLight( 0xffffbb, 0x080820, 1 );
//    uid_lights_raynode.add( ambientLight );

    global_spotLight = new THREE.PointLight( 0x888888, 1, 0, 2 ); // spotLight is global
    global_spotLight.position.set( 100, 1000, 100 );
    global_spotLight.castShadow = false;
    uid_lights_raynode.add( global_spotLight );


    var ambientLight = new THREE.AmbientLight( 0x8e8e8e, 0.8 );
    uid_lights_raynode.add( ambientLight );

    /*
    pointLight1 = new THREE.PointLight( 0x8899aa, 0.5 );
    pointLight1.position.set( 50, 50, 50 );
    uid_lights_raynode.add( pointLight1 );

    pointLight2 = new THREE.PointLight( 0x8899aa, 0.5 );
    pointLight2.position.set( 50, -50, 50 );
    uid_lights_raynode.add( pointLight2 );

    pointLight3 = new THREE.PointLight( 0x8899aa, 0.5 );
    pointLight3.position.set( -50, 0, 50 );
    uid_lights_raynode.add( pointLight3 );

    pointLight4 = new THREE.PointLight( 0x8899aa, 0.5 );
    pointLight4.position.set( -50, 0, -50 );
    uid_lights_raynode.add( pointLight4 );

    var directionalLight2 = new THREE.DirectionalLight(0xaa9988, 0.3);
    directionalLight2.position.set(0, 0, -50);
    uid_lights_raynode.add(directionalLight2);
    */
  }
  else if (light_style == "darkness") {
    var ambientLight = new THREE.AmbientLight( 0xffffff, 0.3 );
    uid_lights_raynode.add( ambientLight );

    var pointLight = new THREE.PointLight( 0xaa5522, 0.5 );
    pointLight.position.z = 2500;
    uid_lights_raynode.add( pointLight );
  }

  animate();
}

export function show_shadows() {
  if (App.state.shadows_visible)
    return;

  // Add shadows light
  shadow_light = new THREE.DirectionalLight(0xaa9988, 0.8);
  shadow_light.position.set(0, 0, 20);
  shadow_light.target = scene;
  shadow_light.castShadow = true;
  shadow_light.shadowDarkness = 0.35;
  shadow_light.shadow.camera.left = -5;
  shadow_light.shadow.camera.right = 5;
  shadow_light.shadow.camera.top = 5;
  shadow_light.shadow.camera.bottom = -5;
  shadow_light.shadow.camera.near = 0;
  shadow_light.shadow.camera.far = 40;
  shadow_light.shadow.mapSize.width = 512;
  shadow_light.shadow.mapSize.height = 512;

  // Add plane for shadows

  // ground
  if (shadow_plane_mesh) {
    uid_shadow_plane_raynode.remove(shadow_plane_mesh);
    uid_shadow_plane_raynode.remove(shadow_plane_grid_1);
    uid_shadow_plane_raynode.remove(shadow_plane_grid_2);
  }

  const planeSize = 500;
  shadow_plane_mesh = new THREE.Mesh( new THREE.PlaneBufferGeometry( planeSize, planeSize ), new THREE.MeshPhongMaterial( { color: 0x444444, depthWrite: false } ) );
  shadow_plane_mesh.receiveShadow = true;
  uid_shadow_plane_raynode.add( shadow_plane_mesh );

  shadow_plane_grid_1 = new THREE.GridHelper( planeSize * 2, planeSize * 2, 0xeeeeee, 0xeeeeee );
  shadow_plane_grid_1.rotation.x = - Math.PI / 2;
  shadow_plane_grid_1.material.opacity = 0.2;
  shadow_plane_grid_1.material.transparent = true;
  uid_shadow_plane_raynode.add( shadow_plane_grid_1 );

  shadow_plane_grid_2 = new THREE.GridHelper( planeSize * 2, planeSize * 2, 0x999999, 0x999999 );
  shadow_plane_grid_2.position.x = 0.5;
  shadow_plane_grid_2.position.y = 0.5;
  shadow_plane_grid_2.rotation.x = - Math.PI / 2;
  shadow_plane_grid_2.material.opacity = 0.2;
  shadow_plane_grid_2.material.transparent = true;
  uid_shadow_plane_raynode.add( shadow_plane_grid_2 );


  shadow_plane_z = -2.5;
  shadow_plane_mesh.position.set(0, 0, shadow_plane_z);
  shadow_plane_grid_1.position.set(0, 0, shadow_plane_z);
  shadow_plane_grid_2.position.set(0.5, 0.5, shadow_plane_z);

  uid_lights_raynode.add(shadow_light);
  scene.add(uid_shadow_plane_raynode);
  App.state.shadows_visible = true;
  update_shadow_light_dimensions(false);
  setTimeout(function() {animate();}, 100);
  animate();
}

export function hide_shadows() {
  if (!App.state.shadows_visible)
    return;

  uid_lights_raynode.remove(shadow_light);
  shadow_light = null;
  scene.remove(uid_shadow_plane_raynode);
  App.state.shadows_visible = false;
  animate();
}

export function update_shadow_position() {
  if (!App.state.shadows_visible)
    return;
  var minz = -2.5;
  uid_vertices.values().forEach(function(v){
    if (uil_is_object_visible(v.id) && v.object3d.position.z < minz)
      minz = v.object3d.position.z;
  });
  if (minz != shadow_plane_z) {
    shadow_plane_z = minz;
    shadow_plane_mesh.position.set(0, 0, shadow_plane_z);
    shadow_plane_grid_1.position.set(0, 0, shadow_plane_z);
    shadow_plane_grid_2.position.set(0.5, 0.5, shadow_plane_z);
  }
}

export function update_shadow_light_dimensions(only_position = false) {
  if (!App.state.shadows_visible)
    return;

  const bb = uid_calculate_global_bounding_box(true, false, false);

  if (!only_position) {
    const d = Math.ceil(bb.dimension*1.2/5)*7.5;
    shadow_light.shadow.camera.left = -d;
    shadow_light.shadow.camera.right = d;
    shadow_light.shadow.camera.top = d;
    shadow_light.shadow.camera.bottom = -d;
  }

  const shadow_plane_z_new = Math.min(-2.5, bb.centerz + (bb.minz - bb.centerz)*1.2);

  if ((shadow_plane_z_new != shadow_plane_z) || !only_position) {
    shadow_plane_z = shadow_plane_z_new;
    shadow_plane_mesh.position.set(0, 0, shadow_plane_z);
    shadow_plane_grid_1.position.set(0, 0, shadow_plane_z);
    shadow_plane_grid_2.position.set(0.5, 0.5, shadow_plane_z);
    shadow_light.shadow.camera.updateProjectionMatrix();
    renderer.shadowMap.needsUpdate = true;
    animate();
  }
}

export function switch_to_material(material) {
  uid_last_used_face_material_name = material;
  refresh_faces_for_material(material, false);

  if (App.state.hidden_edges_visible) {
    display_show_hidden_edges();
  }
  else {
    display_hide_hidden_edges();
  }

  animate();
}

export function switch_to_background(background) {
  App.settings.background = background;
}

export function refresh_face_for_material(fid, material_name, is_transparent, is_wireframe) {
  uid_last_used_face_material_name = material_name;
  var uidf = uid_faces.getItem(fid);
  regenerate_face_material(uidf, material_name, is_transparent, is_wireframe);
}

export function refresh_faces_for_material(material_name, force_all) {
  uid_last_used_face_material_name = material_name;

  // Refresh all selected faces
  var faces_for_new_material;
  if ((uid_selected_faces.length == 0) || force_all)
    faces_for_new_material = uid_faces;
  else
    faces_for_new_material = uid_selected_faces;

  faces_for_new_material.values().forEach(function(f) {
    if (f && f.object3d) {
      refresh_face_for_material(f.id, material_name, false, f.object3d.material[0].wireframe);
    }
    else {
      var fid = f ? f.id : "null";
      console_log_xml("Tried to set material for invalid face " + fid);
    }
  });

  uil_set_face_material(faces_for_new_material.keys(), material_name);
}

export function create_reflection_cube_from_url_string(url_string) {
  if (url_string == "bokeh")
    create_reflection_cube(urls.bokeh);
  else if (url_string == "colors")
    create_reflection_cube(urls.colors);
  else if (url_string == "castle")
    create_reflection_cube(urls.castle);
  else if (url_string == "black")
    create_reflection_cube(urls.black);
  else if (url_string == "iso")
    create_reflection_cube(urls.iso);

  uid_faces.values().forEach(function(f){
    regenerate_face_material(f, f.material_name, uid_face_rendering_type == "transparent", App.state.wireframe_visible);
  });

  setTimeout(function() {
    if (url_string == "bokeh")
      create_reflection_cube(urls.bokeh);
    else if (url_string == "colors")
      create_reflection_cube(urls.colors);
    else if (url_string == "castle")
      create_reflection_cube(urls.castle);
    else if (url_string == "black")
      create_reflection_cube(urls.black);
    else if (url_string == "iso")
      create_reflection_cube(urls.iso);
  }, 100);

  animate();
}

export function create_reflection_cube(urls) {
  reflectionCube = new THREE.CubeTextureLoader().load(urls);
  animate();
}

export function create_face_material(face_id, material, make_transparent, make_wireframe) {
  // Hijack the input material in case it's a sketch face
  if (face_is_for_sketch(face_id) && material != "sketch_material_inside")
    material = "sketch_material";

  // Set default material to the last generated one.
  uid_last_used_face_material_name = material;
  var surf_material;

  if (material == "glass") {
    surf_material = new THREE.MeshLambertMaterial( {
      color: 0xffee00,
      envMap: reflectionCube,
      refractionRatio: 0.95,
      transparent: true,
      opacity: 0.4
    });
  }
  if (material == "sketch_material" || material == "sketch_material_inside") {
    surf_material = new THREE.MeshLambertMaterial( {
      color: 0xaaaaaa,
      // envMap: reflectionCube,
      // refractionRatio: 0.95,
      transparent: true,
      opacity: 0.1
    });
  }
  else if (material == "inside") {
    surf_material = new THREE.MeshPhongMaterial({
      color: 0xFF4444,
      wireframe: false,
      transparent: false,
      depthTest: true,
      depthWrite: true,
      //vertexColors: THREE.FaceColors,
      shininess: 80
    });
  }
  else if (material == "plastic") {
    surf_material = new THREE.MeshPhongMaterial({
      color: 0xffffff,
      wireframe: false,
      transparent: false,
      depthTest: true,
      depthWrite: true,
      //vertexColors: THREE.FaceColors,
      shininess: 80
    });
  }
  else if (material == "obsidian") {
    surf_material = new THREE.MeshStandardMaterial( {
      color: 0xffffff,
      roughness: 0,
      metalness: 0.9,
      wireframe: false,
      transparent: false,
      //displacementMap: displacementMap,
      displacementScale: 2.43,
      displacementBias: -0.428408,
      envMap: reflectionCube,
      envMapIntensity: 1.0 /*,
      clippingPlanes: [ localPlane ],
      clipShadows: true */
    });
  }
  else if (material == "isomaterial") {
    surf_material = new THREE.MeshStandardMaterial( {
      color: 0xBBBBBB,
      roughness: 0,
      metalness: 1,
      wireframe: false,
      transparent: true,
      //displacementMap: displacementMap,
      displacementScale: 2.43,
      displacementBias: -0.428408,
      envMap: reflectionCube,
      envMapIntensity: 1.0
    } );
  }
  else if (material == "nickel") {
    surf_material = new THREE.MeshStandardMaterial( {
      color: 0xFFFFFF,
      roughness: .3,
      metalness: 1,
      wireframe: false,
      transparent: true,
      //displacementMap: displacementMap,
      displacementScale: 2.43,
      displacementBias: -0.428408,
      envMap: reflectionCube,
      envMapIntensity: 1.0
    } );
  }
  else if (material == "enamel" || material == "porcelain") {
    surf_material = new THREE.MeshStandardMaterial( {
      color: 0xFFFFFF,
      roughness: 0.1,
      metalness: 0.2,
      wireframe: false,
      transparent: false,
      //aoMap: aoMap,
      aoMapIntensity: 1,
      //displacementMap: displacementMap,
      //displacementScale: 2.43,
      //displacementBias: -0.428408,
      envMap: reflectionCube,
      envMapIntensity: 1
    } );
  }
  else if (material == "gum") {
    surf_material = new THREE.MeshStandardMaterial( {
      color: 0xDDDDDD,
      roughness: 0.3,
      metalness: .5,
      wireframe: false,
      transparent: true,
      //aoMap: aoMap,
      aoMapIntensity: 1,
      //displacementMap: displacementMap,
      displacementScale: 2.43,
      displacementBias: -0.428408,
      envMap: reflectionCube,
      envMapIntensity: 1
    } );
  }

  if (make_wireframe && !face_is_for_sketch(face_id)) {
    if (material != "inside") {
      surf_material.wireframe = true;
    }
    else {
      surf_material.wireframe = false;
      surf_material.opacity = 0.0;
      surf_material.transparent = true;
      surf_material.visible = false;
    }
  }

  if (make_transparent) {
    if (material != "glass" && material != "sketch_material" && material != "sketch_material_inside" ) {
      surf_material.opacity = 0.6;
      surf_material.transparent = true;
    }
  }
  else {
    if (material != "glass" && material != "sketch_material" && material != "sketch_material_inside" ) {
      surf_material.opacity = 1;
      surf_material.transparent = false;
    }
  }

  //animate();
  return surf_material;
}

export function regenerate_face_material(uidf, new_material_name, make_transparent, make_wireframe) {

  // NOTE: It was found that if we set transparency to False and opacity to any value other than 1, weird patches will appear on faces
  // when running under FFox and windows.

  // Set new material name
  uidf.material_name = new_material_name;
  uidf.setMaterial(new_material_name);

  // Make sure it's not marked as highlighted
  uid_highlighted_object_o3d = null;

  // Create new material
  if (uidf.object3d.material.constructor === Array) {
    if (uidf.getMaterial()) {
      uidf.object3d.material[0] = create_face_material(uidf.id, new_material_name, make_transparent, make_wireframe);;
    }
    if (uidf.getMirrorMaterial()) {
      uidf.object3d.material[2] = create_face_material(uidf.id, new_material_name, make_transparent, make_wireframe);

    }
    if (App.state.inside_red && !face_is_for_sketch(uidf.id)) {
      if (uidf.getInnerMaterial()) {
        uidf.object3d.material[1] = create_face_material(uidf.id, "inside", make_transparent, make_wireframe);
      }
      if (uidf.getMirrorInnerMaterial()) {
        uidf.object3d.material[3] = create_face_material(uidf.id, "inside", make_transparent, make_wireframe);
      }
    } else {
      if (uidf.getInnerMaterial()) {
        uidf.object3d.material[1] = create_face_material(uidf.id, new_material_name, make_transparent, make_wireframe);;
      }
      if (uidf.getMirrorInnerMaterial()) {
        uidf.object3d.material[3] = create_face_material(uidf.id, new_material_name, make_transparent, make_wireframe);;
      }
    }

  }
  else {
    uidf.object3d.material = create_face_material(uidf.id, new_material_name, make_transparent, make_wireframe);;
  }

  if (uidf.getMaterial())
    uidf.getMaterial().side = THREE.FrontSide;
  if (uidf.getInnerMaterial())
    uidf.getInnerMaterial().side = THREE.BackSide;
  if (uidf.getMirrorMaterial())
    uidf.getMirrorMaterial().side = THREE.BackSide;
  if (uidf.getMirrorInnerMaterial())
    uidf.getMirrorInnerMaterial().side = THREE.FrontSide;

  uidf.object3d.castShadow = true;

  set_default_face_color(uidf.id);
  uidf.object3d.geometry.colorsneedsUpdate = true;
}

export function  make_material_single_sided_for_2_face_bodies(object_array) {
  var modified_face_bodies = new HashTable();
  object_array.forEach(function(id) {
    if (isFace(id))
      modified_face_bodies.setItem(uil_get_body_of_face(id), 0);
    else if (isEdge(id))
      modified_face_bodies.setItem(uil_get_body_of_edge(id), 0);
    else if (isVertex(id))
      modified_face_bodies.setItem(uil_get_body_of_vertex(id), 0);
  });
  uid_faces.keys().forEach(function(fid){
    var bid = uil_get_body_of_face(fid);
    if (modified_face_bodies.hasItem(bid))
      modified_face_bodies.setItem(bid, modified_face_bodies.getItem(bid)+1);
  });
  uid_faces.keys().forEach(function(fid){
    var bid = uil_get_body_of_face(fid);
    if (modified_face_bodies.getItem(bid) == 2)
      uid_faces.getItem(fid).object3d.material.side = THREE.FrontSide;
  });
}


export function make_all_faces_wireframe() {
  if (uid_state.shaders_on)
    return;

  uid_faces.values().forEach(function(f) {
    if (!face_is_for_sketch(f.id)) {
      f.getMaterial().wireframe = true;
      f.getInnerMaterial().wireframe = true;
    }
  });
}

export function make_all_faces_solid() {
  if (uid_state.shaders_on)
    return;

    uid_faces.values().forEach(function(f) {
    if (!face_is_for_sketch(f.id)) {
      f.getMaterial().wireframe = false;
      f.getInnerMaterial().wireframe = false;
    }
  });
}

export function make_all_faces_semitransparent() {
  if (uid_state.shaders_on)
    return;

  uid_faces.values().forEach(function(f) {
    if (!face_is_for_sketch(f.id)) {
      regenerate_face_material(f, f.material_name, true, App.state.wireframe_visible);
      if (f.getMaterial()) {
        f.getMaterial().depthTest = true;
        f.getMaterial().depthWrite = false; // XXX
      }
      if (f.getInnerMaterial()) {
        f.getInnerMaterial().depthTest = true;
        f.getInnerMaterial().depthWrite = false; // XXX
      }
      if (f.getMirrorMaterial()) {
        f.getMirrorMaterial().depthTest = true;
        f.getMirrorMaterial().depthWrite = false; // XXX
      }
      if (f.getMirrorInnerMaterial()) {
        f.getMirrorInnerMaterial().depthTest = true;
        f.getMirrorInnerMaterial().depthWrite = false; // XXX
      }
    }
  });
  uid_face_rendering_type = "transparent";
  animate();
}

export function make_all_faces_opaque() {
  if (uid_state.shaders_on)
    return;

  uid_faces.values().forEach(function(f) {
    if (!face_is_for_sketch(f.id)) {
      regenerate_face_material(f, f.material_name, false, App.state.wireframe_visible);
      if (f.getMaterial()) {
        f.getMaterial().depthTest = true;
        f.getMaterial().depthWrite = true; // XXX
      }
      if (f.getInnerMaterial()) {
        f.getInnerMaterial().depthTest = true;
        f.getInnerMaterial().depthWrite = true; // XXX
      }
      if (f.getMirrorMaterial()) {
        f.getMirrorMaterial().depthTest = true;
        f.getMirrorMaterial().depthWrite = true; // XXX
      }
      if (f.getMirrorInnerMaterial()) {
        f.getMirrorInnerMaterial().depthTest = true;
        f.getMirrorInnerMaterial().depthWrite = true; // XXX
      }
    }
  });
  uid_face_rendering_type = "solid";
  animate();
}

export function set_color_of_selected_faces(colorhex, use_new_color, pickr_color_selected_faces, pickr_color_initial_face_colors) {
  for (var i = 0; i < pickr_color_selected_faces.length; i++) {
    var fid = pickr_color_selected_faces[i]
    var f = uid_faces.getItem(fid);
    if (use_new_color) {
      console_log("Setting color");

      if (!colorhex)
        return null;

      f.setColor(colorhex, true);
      console_log("Setting default face color");
      set_default_face_color(fid);
    }
    else {
      console_log("Setting color");
      f.setColor(pickr_color_initial_face_colors[i], true);
      console_log("Setting default face color");
      set_default_face_color(fid);
    }
  }
  animate();
}


// Make an object highlighted.
export function uid_highlight_object(o3d) {
  if (uid_state.shaders_on)
    return;

  // First we distinguish the case of manipulators, which are handled differently.
  if (is_manipulator_geometry(o3d))
    uid_highlight_manipulator(o3d);
  else if (o3d.name == "xy_axes_plane" || o3d.name == "xz_axes_plane" || o3d.name == "yz_axes_plane" ) {
    // It's one of the orthogonal planes, highight it.
    uid_axes_raynode.children.forEach(function(m){
      if (m.name == "xy_frame_mesh" && o3d.name == "xy_axes_plane") {
        m.material.color.setRGB(App.state.colours.highlighted.r/256, App.state.colours.highlighted.g/256, App.state.colours.highlighted.b/256);
      }
      else if (m.name == "yz_frame_mesh" && o3d.name == "yz_axes_plane") {
        m.material.color.setRGB(App.state.colours.highlighted.r/256, App.state.colours.highlighted.g/256, App.state.colours.highlighted.b/256);
      }
      else if (m.name == "xz_frame_mesh" && o3d.name == "xz_axes_plane") {
        m.material.color.setRGB(App.state.colours.highlighted.r/256, App.state.colours.highlighted.g/256, App.state.colours.highlighted.b/256);
      }
      o3d.material.color.set(App.state.colours.highlighted.r/256, App.state.colours.highlighted.g/256, App.state.colours.highlighted.b/256);
    });
  }
  else {
    var uido = uid_object3dIDToUid_object.getItem(o3d.id);
    if (!uido) {
      // This is not a standard ui object, do nothing
    }
    else if (uido.type == "vertex") {
      set_default_vertex_color(uido.id, true);
      animate();
    }
    else if (uido.type == "edge") {
      set_visible_edge_color(uido, App.state.colours.highlighted.r/256, App.state.colours.highlighted.g/256, App.state.colours.highlighted.b/256, true);
      uido.get_visible_edge_mesh().material.linewidth = thick_line_width;
      uido.object3d.material.color.setRGB(App.state.colours.highlighted.r/256, App.state.colours.highlighted.g/256, App.state.colours.highlighted.b/256);
      animate();
    }
    else if (uido.type == "face") {
      uido.getMaterial().color.setRGB(App.state.colours.highlighted.r/256, App.state.colours.highlighted.g/256, App.state.colours.highlighted.b/256);
      if (uido.getInnerMaterial())
        uido.getInnerMaterial().color.setRGB(App.state.colours.highlighted.r/256, App.state.colours.highlighted.g/256, App.state.colours.highlighted.b/256);
      if (uido.getMirrorMaterial())
        uido.getMirrorMaterial().color.setRGB(App.state.colours.highlighted.r/256, App.state.colours.highlighted.g/256, App.state.colours.highlighted.b/256);
      if (uido.getMirrorInnerMaterial())
        uido.getMirrorInnerMaterial().color.setRGB(App.state.colours.highlighted.r/256, App.state.colours.highlighted.g/256, App.state.colours.highlighted.b/256);
      animate();
    }
    else {
      // Error
    }
  }
}

function uid_highlight_manipulator(o3d) {
    const f = App.state.colours.axes.highlight;
    o3d.material.color.setRGB(
        o3d.material.color.r * f,
        o3d.material.color.g * f,
        o3d.material.color.b * f,
    );
}

function unhighlight_orthogonal_plane(obj, raynode=uid_axes_raynode) {
    obj.material.color.set(App.state.colours.axes.body);
    const name = obj.name.replace('axes_plane', 'frame_mesh');
    const mesh = raynode.children.find(x => x.name === name);
    if (!mesh) return;
    mesh.material.color.set(App.state.colours.axes.edges);
}

const is_orthogonal_plane = compose(
    x => x.name,
    inside([
        'xy_axes_plane',
        'xz_axes_plane',
        'yz_axes_plane',
    ]),
);

function unhighlight_manipulator(x) {
    const f = 1/App.state.colours.axes.highlight;
    x.material.color.setRGB(
        x.material.color.r * f,
        x.material.color.g * f,
        x.material.color.b * f,
    );
}

function unhighlight_object(o3d) {
    const uido = uid_object3dIDToUid_object.getItem(o3d.id);
    if (!uido)
        // This is not a standard ui object, we do nothing.
        return;

    switch (uido.type) {
        case 'vertex':
            set_default_vertex_color(uido.id);
            break;

        case 'edge':
            set_default_edge_color(uido.id);
            uido.get_visible_edge_mesh().material.linewidth = default_line_width;
            break;

        case 'face':
            set_default_face_color(uido.id);
            break;

        default:
            // Error
            break;
    }
}

// Make an object un-highlighted.
export function uid_unhighlight_object(o3d) {
    if (uid_state.shaders_on)
        return;
    else if (is_manipulator_geometry(o3d))
        unhighlight_manipulator(o3d);
    else if(is_orthogonal_plane(o3d))
        unhighlight_orthogonal_plane(o3d, uid_axes_raynode);
    else
        unhighlight_object(o3d);
    animate();
}

export function uid_unhighlight_all_axes(
    planes=uid_axes_planes_raynode,
    axes=uid_axes_raynode,
) {

    pipe(
        planes.children,
        filter(is_orthogonal_plane),
        each(x => unhighlight_orthogonal_plane(x, axes))
    );
}

export function uid_unhighlight_all_objects() {
  suppress_animate();

  pipe(
    [ uid_vertices, uid_edges, uid_faces ],
    bind(x => x.values()),
    map(x => x.object3d),
    each(uid_unhighlight_object),
  );

  pipe(
    [ uid_reference_vertices, uid_reference_edges, uid_reference_faces ],
    bind(x => x.values()),
    map(x => x.uid_object.object3d),
    each(uid_unhighlight_object),
  );

  unhighlight_currently_highlighted_object();

  unsuppress_animate_if_ready();
}


export function get_visible_edge_vertices(e) {
  var vertices = new Array();
  var visible_edge_mesh = e.get_visible_edge_mesh();
  for (var i =0; i < visible_edge_mesh.geometry.attributes.position.count; i++) {
    var v = new THREE.Vector3();
    v.x = visible_edge_mesh.geometry.attributes.position.array[i*3];
    v.y = visible_edge_mesh.geometry.attributes.position.array[i*3+1];
    v.z = e.visible_edge_mesh.geometry.attributes.position.array[i*3+2];
    vertices.push(v);
  }
  return vertices;
}

export function create_materials_for_face(uidf, is_reference) {
  // Returns an array of materials for this face under the given circumstances (transparent, wireframe, reference, etc).
  // The elements of the array are: outer material, inner material, [outer mirror material, inner mirror material]
  var surf_material;
  if (is_reference) {
    if (reference_transparent.value) {
      //console.log("reference_transparent is TRUE");
      //console.log("App.settings.reference_transparent = " + App.settings.reference_transparent);
      var rf = uid_reference_faces.getItem(uidf.id);
      var c;
      if (rf.source == "stl")
        c = rgbToHex(ref_stl_face_color_r, ref_stl_face_color_g, ref_stl_face_color_b);
      else if (rf.source == "parasolid")
        c = rgbToHex(ref_parasolid_face_color_r, ref_parasolid_face_color_g, ref_parasolid_face_color_b);
      else /* if (rf.source == "partstudio") */
        c = rgbToHex(ref_partstudio_face_color_r, ref_partstudio_face_color_g, ref_partstudio_face_color_b);
      surf_material = new THREE.MeshPhongMaterial({
        color: c,
        wireframe: App.state.wireframe_visible,
        transparent: true,
        opacity: 0.5,
        depthTest: true,
        depthWrite: true,
        shininess: 30
      });
    }
    else {
      //console.log("reference_transparent is FALSE");
      //console.log("App.settings.reference_transparent = " + App.settings.reference_transparent);
      surf_material = new THREE.MeshPhongMaterial({
        color: rgbToHex(ref_solid_face_color_r, ref_solid_face_color_g, ref_solid_face_color_b),
        wireframe: false,
        transparent: false,
        depthTest: true,
        depthWrite: true,
        shininess: 80
      });
    }
    surf_material.flatShading = true;

    surf_material.side = THREE.DoubleSide;
    return [surf_material];
  }
  else {
    var file_material = uil_get_face_material(uidf.id);
    if (file_material)
      uidf.material_name = file_material;

    if (App.state.wireframe_visible && !face_is_for_sketch(uidf.id)) {
        surf_material = create_face_material(uidf.id, uidf.material_name, false, true);
    }
    else if (uid_face_rendering_type == "transparent") {
      surf_material = create_face_material(uidf.id, uidf.material_name, true, false);
    }
    else {
      surf_material = create_face_material(uidf.id, uidf.material_name, false, false);
    }

    uidf.setMaterial(file_material);
    surf_material.side = THREE.FrontSide;

    var file_colorhex = uil_get_face_color(uidf.id);
    if (file_colorhex)
      uidf.setColor(file_colorhex, false);

    var surf_inner_material;
    if (App.state.inside_red && !face_is_for_sketch(uidf.id))
      surf_inner_material = create_face_material(uidf.id, "inside", uid_face_rendering_type == "transparent", App.state.wireframe_visible);
    else if (face_is_for_sketch(uidf.id))
      surf_inner_material = create_face_material(uidf.id, "sketch_material_inside", uid_face_rendering_type == "transparent", App.state.wireframe_visible);
    else
      surf_inner_material = create_face_material(uidf.id, uidf.material_name, uid_face_rendering_type == "transparent", App.state.wireframe_visible);
    surf_inner_material.side = THREE.BackSide;

    if (uid_state.shaders_on) {
      surf_material = new THREE.ShaderMaterial({
          uniforms: global_uniforms,
          fragmentShader: shaders.wobbleFragmentShader,
          vertexShader: shaders.wobbleVertexShader,
          wireframe: false,
        });

      surf_inner_material.transparent = true;
      surf_inner_material.opacity = 0;
    }

    var surf_materials = [ surf_material, surf_inner_material ];

    if (should_display_mirror_side()) {
      var mirror_material = create_face_material(uidf.id, uidf.material_name, uid_face_rendering_type == "transparent", App.state.wireframe_visible);
      mirror_material.side = THREE.BackSide;
      var mirror_inner_material;
      if (App.state.inside_red)
        mirror_inner_material = create_face_material(uidf.id, "inside", uid_face_rendering_type == "transparent", App.state.wireframe_visible);
      else
        mirror_inner_material = create_face_material(uidf.id, uidf.material_name, uid_face_rendering_type == "transparent", App.state.wireframe_visible);
      mirror_inner_material.side = THREE.FrontSide;

      surf_materials.push(mirror_material);
      surf_materials.push(mirror_inner_material);
    }
    return surf_materials;
  }
}


export function hexToRgb(hex) {
  var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

export function rgbToHex(r, g, b) {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

export function rgb2hsv (r, g, b) {
  let rabs, gabs, babs, rr, gg, bb, h, s, v, diff, diffc, percentRoundFn;
  rabs = r / 255;
  gabs = g / 255;
  babs = b / 255;
  v = Math.max(rabs, gabs, babs),
  diff = v - Math.min(rabs, gabs, babs);
  diffc = c => (v - c) / 6 / diff + 1 / 2;
  percentRoundFn = num => Math.round(num * 100) / 100;
  if (diff == 0) {
      h = s = 0;
  } else {
      s = diff / v;
      rr = diffc(rabs);
      gg = diffc(gabs);
      bb = diffc(babs);

      if (rabs === v) {
          h = bb - gg;
      } else if (gabs === v) {
          h = (1 / 3) + rr - bb;
      } else if (babs === v) {
          h = (2 / 3) + gg - rr;
      }
      if (h < 0) {
          h += 1;
      }else if (h > 1) {
          h -= 1;
      }
  }
  return {
      h: Math.round(h * 360),
      s: percentRoundFn(s * 100),
      v: percentRoundFn(v * 100)
  };
}

let was_transparent_before_highlight = false;
export function temporarily_highlight_all_faces() {
    if (App.state.hidden_edges_visible) {
        display_hide_hidden_edges();
        was_transparent_before_highlight = true;
    }
    make_faces_blue();
    animate();
}

export function remove_temporary_highlight_of_faces() {
    restore_face_color();
    if (was_transparent_before_highlight) {
        display_show_hidden_edges();
        was_transparent_before_highlight = false;
    }
}

export function restore_face_color() {
    for (const fobj of uid_faces.values())
        set_default_face_color(fobj.id);
}

export function make_faces_blue() {
    for (const fobj of uid_faces.values())
        fobj.getMaterial().color.setRGB(
            color_face_working_r/256,
            color_face_working_g/256,
            color_face_working_b/256,
        );
    animate();
}
